import { useParams } from 'react-router-dom'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import { RenderFormat } from '@/content-viewer/store/schemas'
import { MimeType } from '@/constants/constants-mime-types'
import { useTitle } from '@/hooks/use-title'
import { brandedAIFriendlyName } from '@/util/enterprise'
import { type ClientFileReference } from '@/agent/references/store/schemas'
import UserFileContentRenderer from '@/user_files/files/components/UserFileContentRenderer'
import { useUserFileReferenceInStandalone } from '../hooks/useUserFileReferenceInStandalone'
import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'

const EMPTY_PDF_HIGHLIGHTS: PaxtonHighlightArea[] = []
const EMPTY_MARKDOWN_HIGHLIGHTS: string[] = []

function oneBasedPageToZeroBased(page: number | undefined): number | undefined {
  if (page === undefined) return undefined
  return Math.max(page - 1, 0)
}

function UserFileReferenceContent({ fileReference }: { fileReference: ClientFileReference }) {
  const viewableContent = fileReference.viewable_content
  if (!viewableContent) {
    return <ContentViewerErrorState message="This file isn't ready to view yet." />
  }

  const startingPage = oneBasedPageToZeroBased(fileReference.page_nums[0])

  if (viewableContent.content_type === MimeType.PDF) {
    return (
      <UserFileContentRenderer
        userFileId={fileReference.user_file_id}
        format={RenderFormat.PDF}
        pdfHighlights={EMPTY_PDF_HIGHLIGHTS}
        markdownHighlights={EMPTY_MARKDOWN_HIGHLIGHTS}
        startingPage={startingPage}
        title={fileReference.filename}
      />
    )
  }

  if (viewableContent.content_type === MimeType.MARKDOWN) {
    return (
      <div className="h-full w-full overflow-y-auto">
        <UserFileContentRenderer
          userFileId={fileReference.user_file_id}
          format={RenderFormat.MARKDOWN}
          pdfHighlights={EMPTY_PDF_HIGHLIGHTS}
          markdownHighlights={EMPTY_MARKDOWN_HIGHLIGHTS}
          startingPage={startingPage}
          title={fileReference.filename}
        />
      </div>
    )
  }

  return <ContentViewerErrorState message="This file type can't be previewed." />
}

export default function StandaloneReferenceViewerUserFilePage() {
  const { matterId = '', referenceId = '' } = useParams<{ matterId: string; referenceId: string }>()
  const { data, isLoading, isError, refetch } = useUserFileReferenceInStandalone(referenceId, matterId)

  const title = data?.file_reference?.filename ?? `${brandedAIFriendlyName} Reference Viewer`
  useTitle(title)

  return (
    <div className="grow flex flex-col h-full overflow-y-scroll pb-4 pt-8">
      {isLoading && (
        <div className="flex justify-center items-center flex-1">
          <CircularProgressContinuousSized size={25} thickness={5} />
        </div>
      )}

      {isError && !isLoading && (
        <div className="pl-2">
          <ContentViewerErrorState message="Could not load this reference." onRetry={refetch} />
        </div>
      )}

      {data && !isLoading && !isError && (
        <div className="w-full max-w-5xl mx-auto flex flex-col h-full min-h-0 gap-4">
          <div className="pl-2 flex items-center justify-between gap-4">
            <h1 className="text-xl font-bold leading-7 truncate text-brand-neutral-950">{data.file_reference?.filename ?? 'File reference'}</h1>
          </div>

          <div className="min-h-0 flex-1 bg-white border border-brand-neutral-200">
            {data.file_reference ? (
              <UserFileReferenceContent fileReference={data.file_reference} />
            ) : (
              <ContentViewerErrorState message="This file reference cannot be displayed by this version of the app." />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
